<!-- 
  SideBar Component
  
  Props:
  - isUnifiedApp: boolean - Flag to indicate if this is a unified app
  - r7eApp: Object - R7eApp instance
  - getNavBarData: Function - Function that returns a DefaultNavBarData instance
  
  Usage:
  <SideBar 
    :isUnifiedApp="true" 
    :r7eApp="r7eApp"
    :getNavBarData="() => new MyCustomNavBarData()" 
  />
  
  The getNavBarData function should return an object with:
  - title_id(): string - Translation key for the sidebar title
  - title(): string|null - Direct title text (optional)
  - items(): array - Array of navigation items with route, title_id, description, and condition
-->
<template>
  <aside
    class="sidebar"
    :class="{ 'collapsed': isCollapsed }"
  >
    <div class="sidebar-header">
      <a
        class="logo title-link"
        :href="isCollapsed ? '/' : service_url('website')"
      >
        <img
          :src="logoSrc"
          alt="Recognyze"
        >
      </a>
      <div
        class="header-content"
        v-if="!isCollapsed"
      >
        <a 
          class="title-link" 
          href="/"
        >
          {{ navbar.title_id ? t(navbar.title_id) : (navbar.title ? navbar.title : '') }}
        </a>
        <div
          v-if="isAuthenticated"
          class="notifications-sidebar-item"
        >
          <NotificationsWidget
            :r7e-app="props.r7eApp"
            :is-sidebar="true"
          />
        </div>
      </div>
      <div
        class="header-content collapsed-header"
        v-else
      >
        <!-- Apps Dropdown in header when collapsed -->
        <div
          class="dropdown apps-dropdown-header"
          :class="{ 'dropdown-open': appsDropdownOpen }"
          ref="appsDropdownHeaderContainer"
        >
          <a
            href="#"
            class="apps-trigger-header"
            @click="toggleAppsDropdown"
            :title="t('navbar.apps.title')"
            ref="appsDropdownHeaderTrigger"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect
                x="3"
                y="3"
                width="7"
                height="7"
              />
              <rect
                x="14"
                y="3"
                width="7"
                height="7"
              />
              <rect
                x="14"
                y="14"
                width="7"
                height="7"
              />
              <rect
                x="3"
                y="14"
                width="7"
                height="7"
              />
            </svg>
          </a>
          <div 
            class="dropdown-menu apps-menu collapsed-dropdown"
            :style="appsDropdownHeaderStyle"
            v-if="appsDropdownOpen"
          >
            <template 
              v-for="role in availableAppRoles" 
              :key="role" 
            >
              <div
                class="app-entry" 
                :title="service_url(role)"
                :class="{ 'active': r7eApp.account_type === role, 'disabled': unavailable_services.includes(role) }"
                @click="switch_to_app(role, $event)"
              >            
                <div
                  class="app-icon"
                  :class="`${role}-icon`"
                >
                  <svg
                    v-if="role === 'publisher'"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M12 2L2 7l10 5 10-5-10-5z" />
                    <path d="M2 17l10 5 10-5" />
                    <path d="M2 12l10 5 10-5" />
                  </svg>
                  <svg
                    v-else-if="role === 'author'"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z" />
                    <line
                      x1="16"
                      y1="8"
                      x2="2"
                      y2="22"
                    />
                    <line
                      x1="17.5"
                      y1="15"
                      x2="9"
                      y2="6.5"
                    />
                  </svg>
                  <svg
                    v-else-if="role === 'consumer'"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                    <rect
                      x="8"
                      y="2"
                      width="8"
                      height="4"
                      rx="1"
                      ry="1"
                    />
                  </svg>
                  <svg
                    v-else
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                    />
                    <path d="M12 6v6l4 2" />
                  </svg>
                </div>
                <div class="app-info">
                  <div class="app-name">
                    {{ t(`navbar.apps.${role}`) }}
                  </div>
                  <div
                    class="app-description"
                    v-if="r7eApp.account_type === role"
                  >
                    {{ t(`navbar.apps.currentApplicationDesc`) }}
                  </div>
                  <div
                    class="app-description"
                    v-else-if="unavailable_services.includes(role)"
                  >
                    {{ t(`navbar.apps.unavailableServiceDesc`) }}
                  </div>
                  <div
                    class="app-description"
                    v-else
                  >
                    {{ t(`navbar.apps.${role}Desc`) }}
                  </div>
                </div>
              </div>
            </template>
            <div 
              class="app-entry" 
              @click="switch_to_app('website', $event)" 
              :title="service_url('website')"
              :class="{ 'disabled': unavailable_services.includes('website') }"
            >
              <div class="app-icon web-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                  />
                  <line
                    x1="2"
                    y1="12"
                    x2="22"
                    y2="12"
                  />
                  <path d="M12 2a15.3 15.3 0 0 1 0 20" />
                  <path d="M12 2a15.3 15.3 0 0 0 0 20" />
                </svg>
              </div>
              <div class="app-info">
                <div class="app-name">
                  {{ t('navbar.apps.website') }}
                </div>
                <div
                  class="app-description"
                  v-if="unavailable_services.includes('website')"
                >
                  {{ t('navbar.apps.unavailableServiceDesc') }}
                </div>
                <div
                  class="app-description"
                  v-else
                >
                  {{ t('navbar.apps.websiteDesc') }}
                </div>
              </div>
            </div>
            <div 
              class="app-entry" 
              @click="switch_to_app('portal', $event)" 
              :title="service_url('portal')"
              :class="{ 'disabled': unavailable_services.includes('portal') }"
              v-if="r7eApp.isUIDebugEnabled()"
            >
              <div class="app-icon web-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect
                    x="2"
                    y="3"
                    width="20"
                    height="8"
                    rx="2"
                    ry="2"
                  />
                  <rect
                    x="2"
                    y="13"
                    width="20"
                    height="8"
                    rx="2"
                    ry="2"
                  />
                  <line
                    x1="6"
                    y1="7"
                    x2="6"
                    y2="7"
                  />
                  <line
                    x1="6"
                    y1="17"
                    x2="6"
                    y2="17"
                  />
                </svg>
              </div>
              <div class="app-info">
                <div class="app-name">
                  {{ t('navbar.apps.portal') }}
                </div>
                <div
                  class="app-description"
                  v-if="unavailable_services.includes('portal')"
                >
                  {{ t('navbar.apps.unavailableServiceDesc') }}
                </div>
                <div
                  class="app-description"
                  v-else
                >
                  {{ t('navbar.apps.portalDesc') }}
                </div>
              </div>
            </div>          
          </div>
        </div>
        <div
          v-if="isAuthenticated"
          class="notifications-sidebar-item collapsed-notifications"
        >
          <NotificationsWidget
            :r7e-app="props.r7eApp"
            :is-sidebar="true"
          />
        </div>
      </div>
      <button
        class="collapse-toggle"
        @click="toggleCollapse"
        :aria-label="isCollapsed ? (t('sidebar.expand') || 'Expand sidebar') : (t('sidebar.collapse') || 'Collapse sidebar')"
        :title="isCollapsed ? (t('sidebar.expand') || 'Expand sidebar') : (t('sidebar.collapse') || 'Collapse sidebar')"
        :disabled="isMobile"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            v-if="!isCollapsed"
            d="M15 18l-6-6 6-6"
          />
          <path
            v-else
            d="M9 18l6-6-6-6"
          />
        </svg>
      </button>
      <button
        class="hamburger"
        @click="toggleMobileMenu"
        aria-label="Toggle navigation"
      >
        <span :class="{ 'open': mobileMenuOpen }" />
        <span :class="{ 'open': mobileMenuOpen }" />
        <span :class="{ 'open': mobileMenuOpen }" />
      </button>
    </div>
    <ul
      class="sidebar-links"
      :class="{ 'mobile-open': mobileMenuOpen }"
    >
      <!-- Account dropdown - first menu item -->
      <li
        class="dropdown account-dropdown"
        v-if="hasAccessToCurrentService"
        :class="{ 'dropdown-open': userDropdownOpen }"
        ref="userDropdownContainer"
      >
        <a
          href="#"
          class="dropdown-trigger"
          @click="toggleUserDropdown"
          :title="isCollapsed && accountUsername ? (accountName ? accountName : accountUsername) : ''"
          ref="userDropdownTrigger"
        >
          <span class="dropdown-trigger-content">
            <i class="fas fa-user nav-item-icon" /> 
            <span
              class="username"
              v-if="accountUsername && !isCollapsed"
            >
              {{ accountName ? accountName : accountUsername }}
            </span>
          </span>
          <i 
            v-if="!isCollapsed"
            class="fas dropdown-chevron"
            :class="userDropdownOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
          />
        </a>
        <div 
          class="dropdown-menu"
          :class="{ 'collapsed-dropdown': isCollapsed }"
          :style="userDropdownStyle"
          v-if="userDropdownOpen"
        >
          <router-link to="/account">
            <i class="fas fa-user-circle" /> {{ t('navbar.account') }}
          </router-link>
          <router-link to="/settings">
            <i class="fas fa-cog" /> {{ t('navbar.settings') }}
          </router-link>
          <a
            href="#"
            @click.prevent="logout"
          >
            <i class="fas fa-sign-out-alt" /> {{ t('navbar.logout') }}
          </a>
        </div>
      </li>
      <li v-else>
        <a
          href="#"
          @click.prevent="login"
          :title="isCollapsed ? t('navbar.login') : ''"
          class="nav-item-link"
        >
          <i 
            :class="['nav-item-icon', getIconClass('fa-solid fa-user')]"
          />
          <span v-if="!isCollapsed">{{ t('navbar.login') }}</span>
        </a>
      </li>
      
      <template
        v-for="rt in navbar.items"
        :key="rt.route"
      >
        <li 
          v-if="rt.condition && isFeatureAvailable(rt)"
          :class="{ 'has-description': hasDescription(rt) }"
          @mouseenter="hasDescription(rt) && !isMobile ? showDescription($event, rt.route) : null"
          @mouseleave="hasDescription(rt) && !isMobile ? hideDescription(rt.route) : null"
        >
          <router-link 
            :to="rt.route"
            :title="isCollapsed ? (rt.title_id ? t(rt.title_id) : (rt.title ? rt.title : '')) : ''"
            class="nav-item-link"
          >
            <i 
              v-if="rt.icon"
              :class="[getIconClass(rt.icon), 'nav-item-icon']"
            />
            <span v-if="!isCollapsed">
              {{ rt.title_id ? t(rt.title_id) : (rt.title ? rt.title : '') }}
            </span>
          </router-link>
          <div 
            v-if="r7eApp.isHelpEnabled() && hasDescription(rt) && !isMobile && isDescriptionHovered(rt.route)"
            class="dropdown-menu description-dropdown"
            :style="getDescriptionStyle(rt.route)"
            @mouseenter="showDescription($event, rt.route)"
            @mouseleave="hideDescription(rt.route)"
          >
            <div class="description-content">
              {{ getDescription(rt) }}
            </div>
          </div>
        </li>
      </template>
      <li>
        <router-link 
          to="/docs"
          :title="isCollapsed ? t('navbar.docs') : ''"
          class="nav-item-link"
        >
          <i 
            :class="['nav-item-icon', getIconClass('fa-solid fa-book')]"
          />
          
          <span v-if="!isCollapsed">{{ t('navbar.docs') }}</span>
        </router-link>
      </li>
      <!-- Apps Dropdown - only shown when not collapsed (moved to header when collapsed) -->
      <li
        v-if="!isCollapsed"
        class="dropdown apps-dropdown"
        :class="{ 'dropdown-open': appsDropdownOpen }"
      >
        <a
          href="#"
          class="dropdown-trigger apps-trigger"
          @click="toggleAppsDropdown"
        >
          <span class="dropdown-trigger-content">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect
                x="3"
                y="3"
                width="7"
                height="7"
              />
              <rect
                x="14"
                y="3"
                width="7"
                height="7"
              />
              <rect
                x="14"
                y="14"
                width="7"
                height="7"
              />
              <rect
                x="3"
                y="14"
                width="7"
                height="7"
              />
            </svg>
            <!-- <span class="apps-label">{{ t('navbar.apps') }}</span> -->
          </span>
          <i 
            class="fas dropdown-chevron"
            :class="appsDropdownOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
          />
        </a>
        <div 
          class="dropdown-menu apps-menu"
          v-if="appsDropdownOpen"
        >
          <template 
            v-for="role in availableAppRoles" 
            :key="role" 
          >
            <div
              class="app-entry" 
              :title="service_url(role)"
              :class="{ 'active': r7eApp.account_type === role, 'disabled': unavailable_services.includes(role) }"
              @click="switch_to_app(role)"
            >            
              <div
                class="app-icon"
                :class="`${role}-icon`"
              >
                <svg
                  v-if="role === 'publisher'"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
                <svg
                  v-else-if="role === 'author'"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z" />
                  <line
                    x1="16"
                    y1="8"
                    x2="2"
                    y2="22"
                  />
                  <line
                    x1="17.5"
                    y1="15"
                    x2="9"
                    y2="6.5"
                  />
                </svg>
                <svg
                  v-else-if="role === 'consumer'"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                  <rect
                    x="8"
                    y="2"
                    width="8"
                    height="4"
                    rx="1"
                    ry="1"
                  />
                </svg>
                <svg
                  v-else
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                  />
                  <path d="M12 6v6l4 2" />
                </svg>
              </div>
              <div class="app-info">
                <div class="app-name">
                  {{ t(`navbar.apps.${role}`) }}
                </div>
                <div
                  class="app-description"
                  v-if="r7eApp.account_type === role"
                >
                  {{ t(`navbar.apps.currentApplicationDesc`) }}
                </div>
                <div
                  class="app-description"
                  v-else-if="unavailable_services.includes(role)"
                >
                  {{ t(`navbar.apps.unavailableServiceDesc`) }}
                </div>
                <div
                  class="app-description"
                  v-else
                >
                  {{ t(`navbar.apps.${role}Desc`) }}
                </div>
              </div>
            </div>
          </template>
          <div 
            class="app-entry" 
            @click="switch_to_app('website')" 
            :title="service_url('website')"
            :class="{ 'disabled': unavailable_services.includes('website') }"
          >
            <div class="app-icon web-icon">
              <!-- Main website icon: globe -->
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                />
                <line
                  x1="2"
                  y1="12"
                  x2="22"
                  y2="12"
                />
                <path d="M12 2a15.3 15.3 0 0 1 0 20" />
                <path d="M12 2a15.3 15.3 0 0 0 0 20" />
              </svg>
            </div>
            <div class="app-info">
              <div class="app-name">
                {{ t('navbar.apps.website') }}
              </div>
              <div
                class="app-description"
                v-if="unavailable_services.includes('website')"
              >
                {{ t('navbar.apps.unavailableServiceDesc') }}
              </div>
              <div
                class="app-description"
                v-else
              >
                {{ t('navbar.apps.websiteDesc') }}
              </div>
            </div>
          </div>
          <div 
            class="app-entry" 
            @click="switch_to_app('portal')" 
            :title="service_url('portal')"
            :class="{ 'disabled': unavailable_services.includes('portal') }"
            v-if="r7eApp.isUIDebugEnabled()"
          >
            <div class="app-icon web-icon">
              <!-- Main portal icon: backend/portal -->
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect
                  x="2"
                  y="3"
                  width="20"
                  height="8"
                  rx="2"
                  ry="2"
                />
                <rect
                  x="2"
                  y="13"
                  width="20"
                  height="8"
                  rx="2"
                  ry="2"
                />
                <line
                  x1="6"
                  y1="7"
                  x2="6"
                  y2="7"
                />
                <line
                  x1="6"
                  y1="17"
                  x2="6"
                  y2="17"
                />
              </svg>
            </div>
            <div class="app-info">
              <div class="app-name">
                {{ t('navbar.apps.portal') }}
              </div>
              <div
                class="app-description"
                v-if="unavailable_services.includes('portal')"
              >
                {{ t('navbar.apps.unavailableServiceDesc') }}
              </div>
              <div
                class="app-description"
                v-else
              >
                {{ t('navbar.apps.portalDesc') }}
              </div>
            </div>
          </div>          
        </div>
      </li>
      <!-- Mobile Footer Buttons (shown only in hamburger menu) -->
      <li
        class="mobile-footer-item"
        v-if="r7eApp.isHelpEnabled()"
      >
        <button
          v-if="hasAnyDescriptions"
          class="descriptions-toggle mobile-toggle"
          @click="toggleShowAllDescriptions"
          :aria-label="showAllDescriptions ? (t('sidebar.hideDescriptions') || 'Hide descriptions') : (t('sidebar.showDescriptions') || 'Show descriptions')"
          :title="showAllDescriptions ? (t('sidebar.hideDescriptions') || 'Hide descriptions') : (t('sidebar.showDescriptions') || 'Show descriptions')"
        >
          <i
            class="fas"
            :class="showAllDescriptions ? 'fa-eye-slash' : 'fa-eye'"
          />
          <span>{{ showAllDescriptions ? (t('sidebar.hideDescriptions') || 'Hide descriptions') : (t('sidebar.showDescriptions') || 'Show descriptions') }}</span>
        </button>
      </li>
      <li class="mobile-footer-item">
        <button
          class="theme-toggle mobile-toggle"
          @click="toggleTheme"
          :aria-label="theme === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark')"
        >
          <svg
            v-if="theme === 'dark'"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
          </svg>
          <svg
            v-else
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle
              cx="12"
              cy="12"
              r="5"
            />
            <path d="M12 1v2m0 18v2m11-11h-2M3 12H1m16.95 6.95-1.41-1.41M6.34 6.34 4.93 4.93m12.02 0-1.41 1.41M6.34 17.66l-1.41 1.41" />
          </svg>
          <span>{{ theme === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark') }}</span>
        </button>
      </li>
      <li class="mobile-footer-item">
        <router-link
          to="/settings"
          class="settings-toggle mobile-toggle"
          :aria-label="t('navbar.settings')"
        >
          <i class="fas fa-cog" />
          <span>{{ t('navbar.settings') }}</span>
        </router-link>
      </li>
    </ul>
    <!-- Sidebar Footer -->
    <div class="sidebar-footer">
      <!-- Show/Hide Descriptions Toggle -->
      <button
        v-if="r7eApp.isHelpEnabled() && !isCollapsed && hasAnyDescriptions"
        class="descriptions-toggle"
        @click="toggleShowAllDescriptions"
        :aria-label="showAllDescriptions ? (t('sidebar.hideDescriptions') || 'Hide descriptions') : (t('sidebar.showDescriptions') || 'Show descriptions')"
        :title="showAllDescriptions ? (t('sidebar.hideDescriptions') || 'Hide descriptions') : (t('sidebar.showDescriptions') || 'Show descriptions')"
      >
        <i
          class="fas"
          :class="showAllDescriptions ? 'fa-eye-slash' : 'fa-eye'"
        />
      </button>
      <!-- Theme Toggle -->
      <button
        class="theme-toggle"
        @click="toggleTheme"
        :aria-label="theme === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark')"
      >
        <svg
          v-if="theme === 'dark'"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
        </svg>
        <svg
          v-else
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle
            cx="12"
            cy="12"
            r="5"
          />
          <path d="M12 1v2m0 18v2m11-11h-2M3 12H1m16.95 6.95-1.41-1.41M6.34 6.34 4.93 4.93m12.02 0-1.41 1.41M6.34 17.66l-1.41 1.41" />
        </svg>
      </button>
      <!-- Settings Button -->
      <router-link
        to="/settings"
        class="settings-toggle"
        :aria-label="t('navbar.settings')"
        :title="t('navbar.settings')"
      >
        <i class="fas fa-cog" />
      </router-link>
    </div>
  </aside>
  <div
    v-if="hasPendingRegistration && $route.path !== '/profile-setup-REMOVE'"
    class="pending-registration-banner"
  >
    {{ t('navbar.pendingRegistrationDesc') }}
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { ref, onMounted, onUnmounted, defineProps, computed, nextTick, watch } from 'vue';
// import logo from '../assets/logo.png';
import { logoDark } from '@r7e/shared';
import { localStorageManager, DEBUG_MODE } from '@r7e/shared'
import { useI18n } from 'vue-i18n';
// import { useLocaleLoader } from '../composables/useLocaleLoader';
// import { useToast } from "primevue/usetoast";
import NotificationsWidget from './NotificationsWidget.vue';

// const toast = useToast();

// Define props
// isUnifiedApp: boolean flag to indicate if this is a unified app (can be used for conditional logic)
// getNavBarData: function that returns a DefaultNavBarData instance with title_id(), title(), and items() methods
const props = defineProps({
  r7eApp: {
    type: Object,
    required: true
  },
  isUnifiedApp: {
    type: Boolean,
    default: false
  },
  getNavBarData: {
    type: Function,
    required: true
  }
});

/* -------------------- */
/* -- start of setup -- */
/* -------------------- */
class DefaultNavBarData {
  title_id() {  return null; }
  title() {  return null; }
  items() {
    const b = props.r7eApp.hasAccessToCurrentService();
    return [
        { route: "/info", title_id: 'navbar.info', condition: b },
    ]
  }
}
/* -------------------- */
/* --- end of setup --- */
/* -------------------- */

/* DO NOT MODIFY THE REST */

const { t } = useI18n();

const router = useRouter();

const isAuthenticated = ref(props.r7eApp.isAuthenticated());
const hasPendingRegistration = ref(props.r7eApp.hasPendingRegistrationForCurrentService());
const hasAccessToCurrentService = ref(props.r7eApp.hasAccessToCurrentService());
const accountUsername = ref();
const accountName = ref();
const mobileMenuOpen = ref(false);
const userDropdownOpen = ref(false);
const appsDropdownOpen = ref(false);
const hoveredDescriptions = ref(new Set());
const descriptionLinkRefs = ref({});
const descriptionStyles = ref({});

// Mobile detection
const isMobile = ref(window.innerWidth <= 900);

// Function to check and update mobile state
const checkMobileMode = () => {
  const wasMobile = isMobile.value;
  isMobile.value = window.innerWidth <= 900;
  
  // If switching to mobile mode, force sidebar to expanded state and clear descriptions
  if (!wasMobile && isMobile.value) {
    if (isCollapsed.value) {
      isCollapsed.value = false;
      localStorageManager.setItem('sidebarCollapsed', 'false');
    }
    hoveredDescriptions.value.clear();
  }
  
  // Update description positions on resize
  if (!isMobile.value) {
    for (const route of hoveredDescriptions.value) {
      updateDescriptionPosition(route);
    }
  }
};
const showAllDescriptions = ref(localStorageManager.getItem('showAllDescriptions') === 'true');
const isCollapsed = ref(localStorageManager.getItem('sidebarCollapsed') === 'true');

const userDropdownTrigger = ref(null);
const userDropdownContainer = ref(null);
const userDropdownStyle = ref({});

const appsDropdownHeaderTrigger = ref(null);
const appsDropdownHeaderContainer = ref(null);
const appsDropdownHeaderStyle = ref({});

const unavailable_services = ref([]);

const isFeatureAvailable = (rt) => {
  if (rt.experiment) {
    return props.r7eApp.isFeatureAvailable(rt.experiment);
  }
  return true;
}

const hasDescription = (rt) => {
  return (rt.description !== undefined && rt.description !== null && rt.description !== '');
}

const getDescription = (rt) => {
  if (rt.description) {
    return t(rt.description);
  }
  return rt.description || '';
}

const getDescriptionStyle = (route) => {
  return descriptionStyles.value[route] || {};
}

const getIconClass = (icon) => {
  if (!icon) return '';
  
  // Trim whitespace and normalize multiple spaces to single space
  icon = icon.trim().replace(/\s+/g, ' ');
  
  // If icon already contains a space, it's a full class string
  if (icon.includes(' ')) {
    // Normalize Font Awesome 6 format to Font Awesome 5 format
    // This project uses Font Awesome 5 (fas, far, fab) based on other icons in the file
    // fa-solid fa-list -> fas fa-list
    // fa-regular fa-list -> far fa-list
    // fa-brands fa-list -> fab fa-list
    let normalized = icon
      .replace(/^fa-solid\s+fa-/, 'fas fa-')
      .replace(/^fa-regular\s+fa-/, 'far fa-')
      .replace(/^fa-brands\s+fa-/, 'fab fa-')
      .replace(/^fa-light\s+fa-/, 'fal fa-')
      .replace(/^fa-duotone\s+fa-/, 'fad fa-');
    
    // If it's already in FA5 format (fas, far, fab, etc.), return as-is
    if (/^(fas|far|fab|fal|fad)\s+fa-/.test(normalized)) {
      return normalized;
    }
    
    // If normalization didn't change it and it's not FA5 format, try to convert
    // This handles cases like "fa fa-list" -> "fas fa-list"
    if (/^fa\s+fa-/.test(normalized)) {
      return normalized.replace(/^fa\s+fa-/, 'fas fa-');
    }
    
    return normalized;
  }
  
  // Otherwise, assume it's just an icon name and prefix with "fas fa-"
  // Remove any leading "fa-" if accidentally included
  const iconName = icon.startsWith('fa-') ? icon.substring(3) : icon;
  return `fas fa-${iconName}`;
}

// Use the provided function to get navbar data, or fall back to default
const navbarData = props.getNavBarData ? props.getNavBarData() : new DefaultNavBarData();
const navbar = ref({
  title_id: navbarData.title_id(),
  title: navbarData.title(),
  items: navbarData.items()
})

// Get available app roles from r7eApp
const availableAppRoles = computed(() => {
  return props.r7eApp.availableRoles ? props.r7eApp.availableRoles() : ['publisher', 'author', 'consumer'];
});

// Example of how isUnifiedApp could be used for conditional logic
// if (props.isUnifiedApp) {
//   // Apply unified app specific styling or behavior
// }
const on_nav_update_event = () => {
  const updatedNavBarData = props.getNavBarData ? props.getNavBarData() : navbarData;
  navbar.value.title_id = updatedNavBarData.title_id();
  navbar.value.title = updatedNavBarData.title();
  navbar.value.items = updatedNavBarData.items();  
}

const on_authentication_event = (data) => {
  isAuthenticated.value = props.r7eApp.isAuthenticated();
  hasAccessToCurrentService.value = props.r7eApp.hasAccessToCurrentService();

  if (DEBUG_MODE) {
    console.log("SideBar, authentication event", { isAuthenticated: isAuthenticated.value, hasAccessToCurrentService: hasAccessToCurrentService.value, data: data });
  }
  // console.trace();
  if (window.__navbarCheckValidAccountTimeout) {
        clearTimeout(window.__navbarCheckValidAccountTimeout);
  }
  hasPendingRegistration.value = props.r7eApp.hasPendingRegistrationForCurrentService();  

  // Re-compute navbar data using the provided function
  on_nav_update_event();
}

// THEME LOGIC
const theme = ref(localStorageManager.getItem('theme') || 'light');

const applyTheme = (t) => {
  const root = document.documentElement;
  root.classList.remove('theme-dark', 'theme-light');
  root.classList.add(`theme-${t}`);
};

const toggleTheme = () => {
  theme.value = theme.value === 'dark' ? 'light' : 'dark';
  localStorageManager.setItem('theme', theme.value);
  applyTheme(theme.value);
};

function handleStorageChange(event) {
  // console.log("handleStorageChange", event);
  if (event.key === 'theme') {
    theme.value = event.newValue === 'dark' ? 'dark' : 'light';
    applyTheme(theme.value);
  }
}

onMounted(async () => {
  // THEME
  const saved = localStorageManager.getItem('theme');
  theme.value = saved === 'dark' ? 'dark' : 'light';
  applyTheme(theme.value);
  localStorageManager.addEventListener(handleStorageChange);

  // Check mobile mode on mount
  checkMobileMode();
  
  // Add resize listener to detect mobile mode changes
  window.addEventListener('resize', checkMobileMode);
  
  // If on mobile, force sidebar to expanded state
  if (isMobile.value && isCollapsed.value) {
    isCollapsed.value = false;
    localStorageManager.setItem('sidebarCollapsed', 'false');
  }
});

// Watch for dropdown state changes to update position
watch([userDropdownOpen, isCollapsed], () => {
  updateUserDropdownPosition();
});

watch([appsDropdownOpen, isCollapsed], () => {
  updateAppsDropdownHeaderPosition();
});

// Watch for description hover state changes to update position
watch([hoveredDescriptions, isCollapsed], async () => {
  for (const route of hoveredDescriptions.value) {
    await updateDescriptionPosition(route);
  }
});

// Watch for mobile mode changes - force sidebar to expanded on mobile
watch(isMobile, (mobile) => {
  if (mobile) {
    if (isCollapsed.value) {
      isCollapsed.value = false;
      localStorageManager.setItem('sidebarCollapsed', 'false');
    }
    // Clear descriptions when switching to mobile
    hoveredDescriptions.value.clear();
  }
});

// Update CSS custom property for sidebar width
watch(isCollapsed, (collapsed) => {
  document.documentElement.style.setProperty(
    '--sidebar-width',
    collapsed ? '70px' : '280px'
  );
}, { immediate: true });

onUnmounted(() => {
  localStorageManager.removeEventListener(handleStorageChange);
  window.removeEventListener('resize', checkMobileMode);
});

props.r7eApp.registerCredentialsObserver(({ data }) => {
  // console.log("SideBar, credentials observer notified", data);
  accountUsername.value = data?.username || '';
  accountName.value = data?.name || '';
  on_authentication_event (data);
});

const logout = async () => {
  await props.r7eApp.logoutAccount();
  router.push('/login');
};

const login = () => {
  router.push('/login');
};

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

const toggleCollapse = () => {
  // Don't allow collapse on mobile
  if (isMobile.value) {
    return;
  }
  isCollapsed.value = !isCollapsed.value;
  localStorageManager.setItem('sidebarCollapsed', isCollapsed.value.toString());
  // Close all dropdowns when collapsing
  if (isCollapsed.value) {
    userDropdownOpen.value = false;
    appsDropdownOpen.value = false;
    hoveredDescriptions.value.clear();
  }
};

const toggleUserDropdown = async (e) => {
  e.preventDefault();
  userDropdownOpen.value = !userDropdownOpen.value;
  // Close apps dropdown if open
  if (appsDropdownOpen.value) {
    appsDropdownOpen.value = false;
  }
  // Update position for collapsed dropdown
  await updateUserDropdownPosition();
};

const toggleAppsDropdown = async (e) => {
  e.preventDefault();
  appsDropdownOpen.value = !appsDropdownOpen.value;
  // Close user dropdown if open
  if (userDropdownOpen.value) {
    userDropdownOpen.value = false;
  }
  // Check services availability when opening
  if (appsDropdownOpen.value && !appsDropdownChecked) {
    onAppsDropdownOpen();
  }
  // Reset checked state when closing
  if (!appsDropdownOpen.value) {
    appsDropdownChecked = false;
  }
  // Update position for collapsed dropdown
  await updateAppsDropdownHeaderPosition();
};

const showDescription = async (e, route) => {
  hoveredDescriptions.value.add(route);
  // Store the element from the event for positioning
  const targetEl = e.currentTarget;
  if (targetEl) {
    descriptionLinkRefs.value[route] = targetEl;
  }
  await updateDescriptionPosition(route);
};

const hideDescription = (route) => {
  hoveredDescriptions.value.delete(route);
  descriptionStyles.value[route] = {};
  // Clean up the ref
  delete descriptionLinkRefs.value[route];
};

const isDescriptionHovered = (route) => {
  return hoveredDescriptions.value.has(route);
};

const updateDescriptionPosition = async (route) => {
  if (isMobile.value) {
    return;
  }
  
  await nextTick();
  
  const linkEl = descriptionLinkRefs.value[route];
  if (!linkEl || typeof linkEl.getBoundingClientRect !== 'function') {
    return;
  }
  
  const rect = linkEl.getBoundingClientRect();
  const sidebarWidth = isCollapsed.value ? 70 : 280;
  
  descriptionStyles.value[route] = {
    position: 'fixed',
    left: `${sidebarWidth + 4}px`,
    top: `${rect.top}px`,
    width: 'auto',
    minWidth: '200px',
    maxWidth: '300px'
  };
};

const hasAnyDescriptions = computed(() => {
  return navbar.value.items.some(rt => hasDescription(rt));
});

const toggleShowAllDescriptions = () => {
  showAllDescriptions.value = !showAllDescriptions.value;
  localStorageManager.setItem('showAllDescriptions', showAllDescriptions.value.toString());
};

const updateUserDropdownPosition = async () => {
  if (!isCollapsed.value || !userDropdownOpen.value) {
    userDropdownStyle.value = {};
    return;
  }
  
  await nextTick();
  
  if (!userDropdownTrigger.value) {
    userDropdownStyle.value = {};
    return;
  }
  
  const rect = userDropdownTrigger.value.getBoundingClientRect();
  userDropdownStyle.value = {
    position: 'fixed',
    left: `${rect.right + 4}px`,
    top: `${rect.top}px`,
    width: 'auto',
    minWidth: '200px'
  };
};

const updateAppsDropdownHeaderPosition = async () => {
  if (!isCollapsed.value || !appsDropdownOpen.value) {
    appsDropdownHeaderStyle.value = {};
    return;
  }
  
  await nextTick();
  
  if (!appsDropdownHeaderTrigger.value) {
    appsDropdownHeaderStyle.value = {};
    return;
  }
  
  const rect = appsDropdownHeaderTrigger.value.getBoundingClientRect();
  appsDropdownHeaderStyle.value = {
    position: 'fixed',
    left: `${rect.right + 4}px`,
    top: `${rect.top}px`,
    width: 'auto',
    minWidth: '280px'
  };
};

const checkServicesAvailability = async () => {
  // Only check once per open, clear previous state
  unavailable_services.value = [];
  const services = ['website', 'portal'];
  for (const srv of availableAppRoles.value) {
    services.push(srv);
  }
  for (const srv of services) {
    const b = await is_service_available(srv);
    if (!b) {
      unavailable_services.value.push(srv);
    }
  }
}

// Function to switch between different app types
const is_service_available = async (srv) => { 
  return await props.r7eApp.is_service_available(srv);
}

const switch_to_app = (srv, event) => {
  // console.log(`Switching to ${srv} app`);
  
  // Check if Ctrl (Windows/Linux) or Cmd (Mac) key is pressed
  const openInNewTab = event && (event.ctrlKey || event.metaKey);
  
  // You can implement the logic here to switch between different app types
  // For example, redirect to different URLs or trigger app-specific actions
  let url = '';
  switch (srv) {
    case 'website':
      url = service_url ('website');
      if (openInNewTab) {
        window.open(url, '_blank_' + srv);
      } else {
        window.open(url, '_blank_' + srv);
      }
      break;
    case 'portal':
      url = service_url ('portal');
      // console.log(`Switching to ${srv} app: ${url}`);
      if (openInNewTab) {
        window.open(url, '_blank_' + srv);
      } else {
        window.location.href = url;
      }
      break;      
    default:
      url = service_url (srv);
      if (openInNewTab) {
        window.open(url, '_blank_' + srv);
      } else {
        window.location.href = url;
      }
  }
};

const service_url = (srv) => {
  return props.r7eApp.service_url (srv);
}

// Apps dropdown open handler
let appsDropdownChecked = false;
const onAppsDropdownOpen = async () => {
  // Only check if not already checked since last close
  if (!appsDropdownChecked) {
    appsDropdownChecked = true;
    await checkServicesAvailability();
  }
};


// const logoSrc = computed(() => theme.value === 'dark' ? logoDark : logo);
const logoSrc = logoDark; //computed(() => theme.value === 'dark' ? logoDark : logoDark);
</script>

<style scoped>
a.logo img {
  margin-bottom: 1rem;
  padding: 2px;
  height: 70px;
}

.sidebar {
  background-color: var(--color-navbar-bg);
  color: var(--color-navbar-text);
  width: 280px;
  min-width: 280px;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  overflow: visible;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  transition: width 0.3s ease, min-width 0.3s ease;
}

.sidebar.collapsed {
  width: 70px;
  min-width: 70px;
}

.sidebar-header {
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: visible;
}

.sidebar.collapsed .sidebar-header {
  padding: 15px 10px;
}

.header-content {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.collapsed-header {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.apps-dropdown-header {
  position: relative;
  overflow: visible;
}

.apps-trigger-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.3s;
  color: var(--color-navbar-text);
  text-decoration: none;
}

.apps-trigger-header:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.apps-dropdown-header .collapsed-dropdown {
  position: fixed;
  width: auto;
  min-width: 280px;
  box-shadow: 2px 4px 16px rgba(0, 0, 0, 0.3);
  z-index: 1001;
  border-radius: 4px;
  border: 1px solid color-mix(in srgb, var(--color-border), transparent 50%);
  background-color: color-mix(in srgb, var(--color-navbar-bg), black 10%);
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.collapse-toggle {
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  color: var(--color-navbar-text);
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.3s;
  z-index: 10;
}

.sidebar.collapsed .collapse-toggle {
  position: static;
  margin-top: 10px;
  margin-bottom: 10px;
}

.collapse-toggle:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--color-green);
}

.collapse-toggle:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.sidebar-header .logo {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.sidebar.collapsed .sidebar-header .logo {
  margin-bottom: 0;
}

.sidebar.collapsed .sidebar-header .logo img {
  height: 50px;
}

.title-link {
  color: var(--color-navbar-text);
  text-decoration: none;
  transition: color 0.3s;
  text-align: center;
  font-size: 1.2em;
  font-weight: bold;
  word-wrap: break-word;
}

.title-link:hover {
  color: var(--color-green);
}

.hamburger {
  display: none;
  flex-direction: column;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 20;
  position: absolute;
  top: 20px;
  right: 20px;
}

.hamburger span {
  display: block;
  height: 4px;
  width: 100%;
  background: var(--color-text-light);
  margin: 4px 0;
  border-radius: 2px;
  transition: 0.3s;
}

.hamburger span.open:nth-child(1) {
  transform: translateY(8px) rotate(45deg);
}

.hamburger span.open:nth-child(2) {
  opacity: 0;
}

.hamburger span.open:nth-child(3) {
  transform: translateY(-8px) rotate(-45deg);
}

.sidebar-links {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.sidebar-links li {
  margin: 0;
  position: relative;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  overflow: visible;
}

.sidebar-links a {
  color: var(--color-navbar-text);
  text-decoration: none;
  transition: all 0.3s;
  display: inline-flex;
  padding: 16px 20px;
  width: 100%;
  align-items: center;
  justify-content: flex-start;
}

.nav-item-link {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-item-icon {
  flex-shrink: 0;
  width: 20px;
  text-align: center;
  display: inline-block;
  font-style: normal;
  font-variant: normal;
  text-rendering: auto;
  line-height: 1;
}

.sidebar.collapsed .sidebar-links a {
  justify-content: center;
  padding: 16px 10px;
}

.sidebar-links a:hover,
.sidebar-links a.router-link-exact-active {
  background-color: rgba(53, 159, 157, 0.2);
  color: var(--color-green);
  padding-left: 24px;
}

.sidebar.collapsed .sidebar-links a:hover,
.sidebar.collapsed .sidebar-links a.router-link-exact-active {
  padding-left: 10px;
}

.dropdown {
  position: relative;
  overflow: visible;
}

.dropdown-menu {
  display: block;
  position: static;
  background-color: color-mix(in srgb, var(--color-navbar-bg), black 5%);
  width: 100%;
  box-shadow: none;
  z-index: 1;
  border-radius: 0;
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0;
  margin-top: 0;
}

/* Collapsed dropdowns appear on the right as popup menus */
.dropdown-menu.collapsed-dropdown {
  position: fixed;
  width: auto;
  min-width: 200px;
  box-shadow: 2px 4px 16px rgba(0, 0, 0, 0.3);
  z-index: 1001;
  border-radius: 4px;
  border: 1px solid color-mix(in srgb, var(--color-border), transparent 50%);
  background-color: color-mix(in srgb, var(--color-navbar-bg), black 10%);
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.dropdown-menu a {
  color: var(--color-navbar-text);
  padding: 12px 16px 12px 40px;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.dropdown-menu.collapsed-dropdown a {
  padding: 12px 16px;
}

.dropdown-menu a > i:first-child {
  margin-right: 0;
  flex-shrink: 0;
}

.dropdown-menu a:hover {
  background-color: rgba(53, 159, 157, 0.2);
  color: var(--color-green);
  padding-left: 44px;
}

.dropdown-trigger {
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-wrap: nowrap;
}

.sidebar.collapsed .dropdown-trigger {
  justify-content: center;
}

.dropdown-trigger-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  flex-shrink: 1;
}

.dropdown-trigger > i:first-of-type:not(.dropdown-chevron),
.dropdown-trigger > svg:first-of-type {
  flex-shrink: 0;
}

.dropdown-trigger > .dropdown-chevron {
  font-size: 0.8em;
  flex-shrink: 0;
  margin-left: 12px;
  align-self: center;
}

.dropdown-open .dropdown-trigger {
  background-color: rgba(53, 159, 157, 0.2);
  color: var(--color-green);
}

/* Description Styles */
.description-content {
  color: color-mix(in srgb, var(--color-navbar-text), transparent 20%);
  font-style: italic;
  font-size: 0.8em;
  line-height: 1.4;
  white-space: normal;
}

/* Description Dropdown Specific Styles */
.description-dropdown {
  position: fixed;
  width: auto;
  min-width: 200px;
  max-width: 300px;
  box-shadow: 2px 4px 16px rgba(0, 0, 0, 0.3);
  z-index: 1001;
  border-radius: 4px;
  border: 1px solid color-mix(in srgb, var(--color-border), transparent 50%);
  background-color: color-mix(in srgb, var(--color-navbar-bg), black 10%);
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  padding: 12px 16px;
}

.description-toggle {
  background: none;
  border: none;
  color: var(--color-navbar-text);
  cursor: pointer;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;
  font-size: 0.8em;
}

.description-toggle:hover {
  color: var(--color-green);
}

/* Apps Dropdown Specific Styles */
.apps-dropdown .dropdown-menu {
  min-width: auto;
  width: 100%;
  padding: 0;
}

.apps-trigger {
  padding: 16px 20px;
  border-radius: 0;
  transition: background-color 0.3s;
}

.apps-trigger:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.apps-label {
  font-size: 0.9em;
  font-weight: 500;
}

.app-entry.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: var(--color-disabled-bg);
  color: var(--color-disabled-text);
}

.app-entry.active {
  background-color: var(--color-disabled-bg);
  color: var(--color-disabled-text);
}

.app-entry {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.3s;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.app-entry:last-child {
  border-bottom: none;
}

.app-entry:hover {
  background-color: var(--color-green);
  color: var(--color-navbar-text);
}

.app-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  background-color: rgba(255, 255, 255, 0.1);
}

.publisher-icon {
  background-color: rgba(52, 152, 219, 0.2);
  color: #3498db;
}

.author-icon {
  background-color: rgba(155, 89, 182, 0.2);
  color: #9b59b6;
}

.consumer-icon {
  background-color: rgba(46, 204, 113, 0.2);
  color: #2ecc71;
}

.app-info {
  flex: 1;
}

.app-name {
  font-weight: 600;
  font-size: 0.95em;
  margin-bottom: 2px;
}

.app-description {
  font-size: 0.8em;
  opacity: 0.8;
  line-height: 1.3;
}

.username {
  color: var(--color-gold);
  font-weight: bold;
}

/* Sidebar Footer */
.sidebar-footer {
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0;
  display: flex;
  flex-direction: row;
  background-color: color-mix(in srgb, var(--color-navbar-bg), black 5%);
}

.sidebar.collapsed .sidebar-footer {
  flex-direction: column;
}

.descriptions-toggle {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text-light);
  font-size: 0.9em;
  padding: 12px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  border-right: 1px solid rgba(255, 255, 255, 0.05);
}

.sidebar.collapsed .descriptions-toggle {
  border-right: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.descriptions-toggle:hover {
  background-color: rgba(53, 159, 157, 0.2);
  color: var(--color-green);
}

.theme-toggle {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text-light);
  font-size: 0.9em;
  padding: 12px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.sidebar.collapsed .theme-toggle {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.theme-toggle svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.theme-toggle:hover {
  background-color: rgba(53, 159, 157, 0.2);
  color: var(--color-green);
}

.settings-toggle {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text-light);
  font-size: 0.9em;
  padding: 12px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  text-decoration: none;
  border-left: 1px solid rgba(255, 255, 255, 0.05);
}

.sidebar.collapsed .settings-toggle {
  border-left: none;
}

.settings-toggle:hover {
  background-color: rgba(53, 159, 157, 0.2);
  color: var(--color-green);
}

.settings-toggle i {
  font-size: 18px;
  flex-shrink: 0;
}

.settings-toggle.router-link-exact-active {
  background-color: rgba(53, 159, 157, 0.2);
  color: var(--color-green);
}

/* Mobile footer items - hidden on desktop */
.mobile-footer-item {
  display: none;
}

.mobile-toggle {
  width: 100%;
  text-align: left;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.pending-registration-banner {
  font-size: 1.2rem;
  padding: 2rem;
  margin-bottom: 10px;
  background-color: var(--color-required-bg);
  color: var(--color-required-text);
  margin-left: 280px;
  transition: margin-left 0.3s ease;
}

.sidebar.collapsed ~ .pending-registration-banner {
  margin-left: 70px;
}

/* Mobile styles */
@media (max-width: 900px) {
  .sidebar {
    width: 100%;
    height: auto;
    position: relative;
    min-height: auto;
  }

  .sidebar-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
  }

  .sidebar-header .logo {
    margin-bottom: 0;
    margin-right: 1rem;
  }

  .title-link {
    text-align: left;
    font-size: 1em;
  }

  .hamburger {
    display: flex;
    position: static;
  }

  /* Hide collapse button on mobile */
  .collapse-toggle {
    display: none;
  }

  .sidebar-links {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: var(--color-dark-blue);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
    z-index: 10;
    max-height: calc(100vh - 100px);
    overflow-y: auto;
  }

  .sidebar-links.mobile-open {
    display: flex;
  }

  .sidebar-links li {
    width: 100%;
    text-align: left;
  }

  /* Hide sidebar footer on mobile */
  .sidebar-footer {
    display: none;
  }

  /* Show mobile footer items in hamburger menu */
  .mobile-footer-item {
    display: block;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    margin-top: 0;
  }

  .mobile-footer-item:first-of-type {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    margin-top: auto;
  }

  .mobile-toggle:hover {
    background-color: rgba(53, 159, 157, 0.2);
    color: var(--color-green);
  }

  .dropdown-menu {
    position: static;
    box-shadow: none;
    border-radius: 0;
    background: rgba(255, 255, 255, 0.05);
    margin-left: 0;
    margin-top: 0;
    width: 100%;
  }
  
  /* Hide Description Dropdown on Mobile */
  .description-dropdown {
    display: none !important;
  }
  
  /* Mobile Apps Dropdown */
  .apps-dropdown .dropdown-menu {
    min-width: auto;
    width: 100%;
  }
  
  .app-entry {
    padding: 16px;
  }
  
  .app-icon {
    width: 36px;
    height: 36px;
  }

  .pending-registration-banner {
    margin-left: 0;
  }
}

.notifications-sidebar-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.collapsed-header .collapsed-notifications {
  margin-top: 8px;
}

.account-dropdown .dropdown-trigger-content {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>

